import { v4 as uuidv4 } from 'uuid';
import { BaseTool } from '../BaseTool';
import * as THREE from 'three';
import { ThreeEvent } from '@react-three/fiber';
import { Element } from '../../core/types';
import i18n from '../../i18n';

/** Visual size per RPC kind (meters). */
const SITE_KIND_DIMS: Record<string, { radius: number; height: number; color: string }> = {
    Tree: { radius: 1.2, height: 5, color: '#2e8b2e' },
    Shrub: { radius: 0.6, height: 1, color: '#4aa04a' },
    Bench: { radius: 0.4, height: 0.5, color: '#8a6a48' },
    Lamp: { radius: 0.15, height: 4.5, color: '#ffd95e' },
    Car: { radius: 1, height: 1.5, color: '#3f7d9e' },
};

/**
 * Site Component: click-place RPC-style landscape elements (trees, shrubs,
 * benches, lamps, cars). The tool stays active for repeated placement, like
 * BIM; Escape returns to Select.
 */
export class SiteComponentTool extends BaseTool {
    id = 'SITE_COMPONENT';
    name = 'Site Component';
    cursor = 'crosshair';

    protected onActivate(): void {
        this.notify(i18n.t('tools.notify.siteComponentClickTheGroundTo'), 'info');
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (!point) return;
        const snapped = this.snapToGrid(point);
        this.placeComponent(snapped);
        // Don't deactivate, allow multiple placement
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        const point = this.getIntersection(event);
        if (point) {
            const snapped = this.snapToGrid(point);
            this.context.appStore.setToolPreview({
                type: 'point',
                position: snapped,
                color: '#00FF00'
            });
        }
    }

    private placeComponent(position: THREE.Vector3) {
        if (!this.context) return;
        const props = this.context.bimStore.toolProperties;
        const kind = props.siteComponentKind ?? 'Tree';
        const dims = SITE_KIND_DIMS[kind] ?? SITE_KIND_DIMS.Tree;

        const component: Element = {
            id: uuidv4(),
            category: 'Site',
            type: `Site Component: ${kind}`,
            name: `${kind}`,
            parameters: {
                'Kind': kind,
                'Elevation': `${position.z.toFixed(2)} m`,
                'Height': `${dims.height} m`,
            },
            constraints: [],
            geometryData: { position: [position.x, position.y, position.z], kind, radius: dims.radius, height: dims.height },
        };
        this.context.bimStore.addElement(component);
        this.notify(i18n.t('tools.notify.componentPlaced', { kind: kind }), 'success');
    }

    onPointerUp(): void { }

    deactivate(): void {
        this.context?.appStore.setToolPreview(null);
        super.deactivate();
    }
}
